/**
 * McpStaleBadge — shows a small amber warning when an MCP server's tool list
 * may be out of date. The badge appears inline next to the server name inside
 * any workflow node editor that lists MCP servers.
 *
 * "Stale" means:
 *   - lastDiscoveredAt is null  → never auto-synced (server is brand-new or column just added)
 *   - lastDiscoveredAt > 24h ago → tools may have changed since last sync
 *
 * The auto-sync background service runs every 2h so under normal conditions
 * a server is never more than 2h stale. The badge is therefore a meaningful
 * signal — if you see it, something is wrong (server unreachable, new server
 * added before first sync, etc.).
 */
import React from "react";

interface Props {
  lastDiscoveredAt: string | null | undefined;
}

/** Returns true if the server's tool list may be stale. */
export function isMcpStale(lastDiscoveredAt: string | null | undefined): boolean {
  if (!lastDiscoveredAt) return true;
  const diff = Date.now() - new Date(lastDiscoveredAt).getTime();
  return diff > 24 * 60 * 60 * 1000; // > 24 hours
}

/** Friendly elapsed time, e.g. "3h ago", "2d ago", "just now". */
export function mcpSyncAge(lastDiscoveredAt: string | null | undefined): string {
  if (!lastDiscoveredAt) return "never synced";
  const ms = Date.now() - new Date(lastDiscoveredAt).getTime();
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours < 1) return "just now";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Inline badge shown next to an MCP server name when its tool list may be stale.
 * Clicking it links to the MCP server settings page where they can re-sync.
 */
export default function McpStaleBadge({ lastDiscoveredAt }: Props) {
  if (!isMcpStale(lastDiscoveredAt)) return null;

  return (
    <a
      href="/integrations/mcp"
      title={`Tool list may be outdated — last synced: ${mcpSyncAge(lastDiscoveredAt)}. Click to re-sync.`}
      className="inline-flex items-center gap-0.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded hover:bg-amber-100 transition-colors"
      onClick={(e) => e.stopPropagation()}
    >
      ⚠ {mcpSyncAge(lastDiscoveredAt)}
    </a>
  );
}
